<template>
  <div tabindex="-1" id="confirmationView">
    <h2 id="scheduledBranchMeetingHeading">{{ $t('Scheduling the branch meeting:') }}</h2>
    <span class="heading-l" id="scheduledMeetingLocationHeading">
      {{ selectedBranch.name }}
      <span v-if="selectedBranch.wca" style="margin-left: 2px" :aria-label="$t('wheelchair_accessible')">&#xf193;</span>
    </span>
    <h2 class="bold" id="scheduledMeetingTimeHeading">
      <span aria-hidden="true" aria-labelledby="hiddenTime">{{ formattedDate }}, {{ selectedTime.value }}</span>
      <span id="timeSlot">{{ customFormatterScreenReader(formattedDate, selectedTime.value) }}</span>
      <span id="hiddenTime" class="d-none">{{ customFormatterScreenReader(formattedDate, selectedTime.value) }}</span>
      ({{
        $t('Xmin', {
          X: meetingDuration,
        })
      }})
    </h2>
    <error-container v-if="submitError" :errorMsg="submitErrorMessage" :type="'msg'"></error-container>
    <button @click="toBooking()" id="changeDateTimeAddress" type="button" class="link">{{
      $t('Change meeting details')
    }}</button>
    <p class="heading-l" id="personalInformationHeading">{{
      childrenArray.length > 0 ? $t('parent_information') : $t('Contact information')
    }}</p>
    <div class="confirmation-details">
      <div class="confirmation-details__field" v-for="(field, index) in detailsFields" :key="index">
        <template v-if="field.name == 'Personal identity code'">
          <p class="confirmation-details__field__name">{{ $t(field.name) }}:</p>
          <p class="confirmation-details__field__value" style="position: relative">
            <span aria-hidden="true" aria-labelledby="hiddenIdCode">{{ field.value }}</span>
            <span id="hiddenIdCode" class="d-none">{{ formatScreenReaderIdCode(field.value) }}</span>
            <span id="idCodeSlot">{{ formatScreenReaderIdCode(field.value) }}</span>
          </p>
        </template>
        <template v-else>
          <p class="confirmation-details__field__name">{{ $t(field.name) }}:</p>
          <p class="confirmation-details__field__value">{{ field.value }}</p>
        </template>
      </div>
    </div>
    <!-- Children information -->
    <template v-if="childrenArray && childrenArray.length > 0">
      <span class="heading-l" id="personalInformationHeading">{{ $t('childrens_information') }}</span>
      <div
        class="confirmation-details"
        style="margin-bottom: 55px"
        v-for="(children, index) in childrenArray"
        :key="index"
      >
        <div class="confirmation-details__field">
          <p class="confirmation-details__field__name">{{ $t('Name and surname') }}:</p>
          <p class="confirmation-details__field__value">{{ children.name }}</p>
        </div>
        <div class="confirmation-details__field">
          <p class="confirmation-details__field__name">{{ $t('Personal identity code') }}:</p>
          <p class="confirmation-details__field__value" style="position: relative">
            <span aria-hidden="true" aria-labelledby="hiddenIdCode">{{ children.personal_code }}</span>
            <span id="hiddenIdCode" class="d-none">{{ formatScreenReaderIdCode(children.personal_code) }}</span>
            <span id="idCodeSlot">{{ formatScreenReaderIdCode(children.personal_code) }}</span>
          </p>
        </div>
        <div class="confirmation-details__field">
          <p class="confirmation-details__field__name">{{ $t('document_number') }}:</p>
          <p class="confirmation-details__field__value">{{ children.document_number }}</p>
        </div>
      </div>
    </template>
    <!-- Representative information -->
    <template v-if="representativeData && Object.keys(representativeData).length > 0">
      <span class="heading-l" id="personalInformationHeading">{{ $t('representative_information') }}</span>
      <div class="confirmation-details" style="margin-bottom: 55px">
        <div class="confirmation-details__field">
          <p class="confirmation-details__field__name">{{ $t('Name and surname') }}:</p>
          <p class="confirmation-details__field__value">{{ representativeData.name }}</p>
        </div>
        <div class="confirmation-details__field">
          <p class="confirmation-details__field__name">{{ $t('Personal identity code') }}:</p>
          <p class="confirmation-details__field__value" style="position: relative">
            <span aria-hidden="true" aria-labelledby="hiddenIdCode">{{ representativeData.personal_code }}</span>
            <span id="hiddenIdCode" class="d-none">{{
              formatScreenReaderIdCode(representativeData.personal_code)
            }}</span>
            <span id="idCodeSlot">{{ formatScreenReaderIdCode(representativeData.personal_code) }}</span>
          </p>
        </div>
      </div>
    </template>
    <div class="button__wrapper button__wrapper--margin-top-small">
      <button class="button button--secondary" type="button" @click="goBack()">{{ $t('Back') }}</button>
      <button
        class="button button--primary"
        @click="forward()"
        :class="{ 'button--disabled': submitError || appointmentDataLoading }"
        >{{ $t('Book a meeting') }}</button
      >
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useBookingStore, usePersonalStore } from '../store'
import { getCountryURL } from '../utils/countryConfig'
import ErrorContainer from '../components/ErrorContainer.vue'
import moment from 'moment'
export default {
  name: 'PersonalView',
  data() {
    return {
      termsLink: '',
    }
  },
  components: {
    ErrorContainer,
  },
  computed: {
    ...mapState(useBookingStore, [
      'groupValue',
      'selectedDate',
      'selectedTime',
      'selectedBranch',
      'appCountry',
      'appLang',
      'comment',
      'appointmentLang',
      'fail',
      'sessionId',
      'errorMessage',
      'appointmentDataLoading',
      'appointmentType',
      'meetingDuration',
      'childrenCount',
      'childrenArray',
      'representativeData',
    ]),
    ...mapState(usePersonalStore, [
      'fullName',
      'idCode',
      'email',
      'number',
      'companyName',
      'registrationNumber',
      'representativeName',
      'representativeIdCode',
      'countryCode',
      'documentType',
      'documentNumber',
    ]),
    submitError: {
      get() {
        return this.fail
      },
    },
    formattedDate: {
      get() {
        return moment(this.selectedDate).format('DD.MM.YYYY')
      },
    },
    formattedDateScreenReader: {
      get() {
        return moment(this.selectedDate).format('YYYY-MM-DD')
      },
    },
    detailsFields: {
      get() {
        return [
          { name: 'Name and surname', value: this.fullName },
          { name: 'Personal identity code', value: this.idCode },
          { name: 'E-mail', value: this.email },
          { name: 'Phone number', value: this.countryCode + this.number },
        ]
      },
    },
    submitErrorMessage: {
      get() {
        return this.errorMessage !== ''
          ? this.$i18n.t(this.errorMessage)
          : this.$i18n.t('Something went wrong, please submit again')
      },
    },
  },
  watch: {},
  mounted() {
    this.termsLink = getCountryURL(this.appCountry, this.appLang, 'terms') || getCountryURL('all', 'terms', 'default')
    document.getElementById('confirmationView').focus()
  },
  methods: {
    ...mapActions(useBookingStore, ['updateAppointment', 'resetFailState']),
    forward() {
      if (!this.submitError && !this.appointmentDataLoading) {
        this.getAppointmentData()
      }
    },
    goBack() {
      this.resetFailState(false)
      this.$router.push('/personal')
    },
    getAppointmentData() {
      var appointmentDataObject = new FormData()

      appointmentDataObject.append('lang', this.appLang)
      appointmentDataObject.append('country', this.appCountry)
      appointmentDataObject.append('group', this.groupValue)
      appointmentDataObject.append('sub_unit_id', this.selectedBranch.id)
      appointmentDataObject.append('appointment_type_id', this.selectedBranch.appointment_type_id)
      appointmentDataObject.append('date', this.selectedDate)
      appointmentDataObject.append('time_slot', this.selectedTime.time_slot)
      appointmentDataObject.append('customer[name]', this.fullName)
      appointmentDataObject.append('customer[email]', this.email)
      appointmentDataObject.append('customer[personal_code]', this.idCode)
      appointmentDataObject.append('customer[phone]', this.countryCode + this.number)
      appointmentDataObject.append('meeting_lang', this.appointmentLang)
      appointmentDataObject.append('session_id', this.sessionId)

      if (this.comment.length) {
        appointmentDataObject.append('comments', this.comment)
      }
      if (this.groupValue == 'JP') {
        appointmentDataObject.append('customer[company_name]', this.companyName)
        appointmentDataObject.append('customer[registration_number]', this.registrationNumber)
      }
      if (this.documentType !== '' && this.documentNumber !== '') {
        appointmentDataObject.append('document_type', this.documentType)
        appointmentDataObject.append('document_number', this.documentNumber)
      }
      if (this.childrenCount) {
        appointmentDataObject.append('children_count', this.childrenCount)
      }
      if (this.childrenArray) {
        this.childrenArray.forEach((el, index) => {
          appointmentDataObject.append(`customer[children][${index + 1}][name]`, el.name)
          appointmentDataObject.append(`customer[children][${index + 1}][personal_code]`, el.personal_code)
          appointmentDataObject.append(`customer[children][${index + 1}][document_number]`, el.document_number)
        })
      }
      if (this.representativeData) {
        appointmentDataObject.append('customer[representative][name]', this.representativeData.name)
        appointmentDataObject.append('customer[representative][personal_code]', this.representativeData.personal_code)
      }
      this.updateAppointment(appointmentDataObject)
    },
    toBooking() {
      this.resetFailState(false)
      this.$router.push('/')
    },
    toPersonal() {
      this.resetFailState(false)
      this.$router.push('/personal')
    },
    customFormatterScreenReader(date, time) {
      const dateTime = `${date} ${time}`
      const screenReaderDate = moment(dateTime, 'DD.MM.YYYY HH:mm').format('D MMMM, YYYY, h:mm A')
      return screenReaderDate
    },
    formatScreenReaderIdCode(code) {
      return code.split('').join(' ')
    },
  },
}
</script>

<style scoped>
#timeSlot {
  position: absolute;
  left: 0;
  opacity: 0;
  width: 269px;
  height: 4rem;
  overflow: hidden;
  word-wrap: break-word;
}
#idCodeSlot {
  position: absolute;
  left: 0;
  opacity: 0;
  width: 150px;
  height: 1.6rem;
  overflow: hidden;
  word-wrap: break-word;
}
</style>
